/**
 * After a roster save on an ENABLED scheduler study (docs/plans/
 * crm-agent-scheduler-mode-plan.md B5 LATER EDITS; WP-10): everyone on the
 * list can be booked by the scheduling assistant (owner decision 17), so
 * taking someone off it leaves their upcoming assistant-booked calls and their
 * open leads on this study with them. This panel lists them, with the same
 * reassign the callback editor offers: while the lead is still the old
 * recruiter's, a ticked "Also make <new recruiter> the lead's owner" shows
 * before the save, and unticking it moves only the call (WP-10 review L4). It
 * says when the study is no longer ready (for example, nobody left who can be
 * booked). Nothing moves unless a person clicks Reassign.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, UserMinus } from 'lucide-react';
import api from '../../../services/api';
import { Alert } from '../ui';
import { reassignCallBody, removedCallOrigin } from './schedulerLogic';

export interface RemovedCall {
  callback_id: string;
  contact_id: string;
  contact_name: string;
  label: string;
  /** 'agent' (booked by text) or 'public_link' (booked on the scheduling page). */
  booked_via?: string;
  owner_is_assignee: boolean;
  study_unknown?: boolean;
}
export interface RemovedLead { contact_id: string; contact_name: string; lifecycle_status?: string | null }
export interface RemovedMember { user_id: string; name: string | null; calls: RemovedCall[]; leads: RemovedLead[] }
export interface RosterSchedulerReport {
  removed?: RemovedMember[];
  readiness?: { ready: boolean; items: Array<{ key: string; ok: boolean; blocking: boolean; detail: string }> };
  error?: string;
}

const errMsg = (e: any): string =>
  e?.response?.data?.error || e?.response?.data?.detail || e?.message || 'Request failed';

export default function RemovedRecruitersPanel({ report, recruiters, onDismiss }: {
  report: RosterSchedulerReport;
  /** The saved list: who the calls and leads can go to. */
  recruiters: Array<{ user_id: string; name: string | null; email?: string | null }>;
  onDismiss: () => void;
}) {
  const [picks, setPicks] = useState<Record<string, string>>({});
  // "Also make <them> the lead's owner", per call: ticked unless unticked.
  const [keepLead, setKeepLead] = useState<Record<string, boolean>>({});
  const [busy, setBusy] = useState<string | null>(null);
  const [done, setDone] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const nameOf = (id: string) => recruiters.find((r) => r.user_id === id)?.name || recruiters.find((r) => r.user_id === id)?.email || 'them';

  const failing = (report.readiness?.items || []).filter((i) => i.blocking && !i.ok);
  const removed = (report.removed || []).filter((m) => m.calls.length || m.leads.length);
  if (!report.error && !failing.length && !removed.length) return null;

  const reassignCall = async (c: RemovedCall) => {
    const key = `cb:${c.callback_id}`;
    const to = picks[key];
    if (!to || busy) return;
    setBusy(key);
    setError(null);
    try {
      // The callback editor's rule: the lead goes with the call only while it
      // is still the removed recruiter's AND the offer stayed ticked (the
      // server re-checks).
      const res = await api.patch(`/api/crm/callbacks/${c.callback_id}`, reassignCallBody(c, to, keepLead[key] !== false));
      const moved = res.data?.move_owner?.moved === true || res.data?.move_owner?.reason === 'already_owner';
      setDone((d) => ({ ...d, [key]: `Now ${nameOf(to)}'s call${moved ? ', and the lead is theirs too' : ''}.` }));
    } catch (e: any) {
      setError(e?.response?.data?.code === 'callback_changed'
        ? `Someone else changed ${c.contact_name}'s call just now. Open the lead to see it as it is.`
        : errMsg(e));
    } finally {
      setBusy(null);
    }
  };

  const reassignLead = async (l: RemovedLead) => {
    const to = picks[`lead:${l.contact_id}`];
    if (!to || busy) return;
    setBusy(`lead:${l.contact_id}`);
    setError(null);
    try {
      await api.patch(`/api/crm/contacts/${l.contact_id}`, { owner_user_id: to });
      setDone((d) => ({ ...d, [`lead:${l.contact_id}`]: `Now ${nameOf(to)}'s lead.` }));
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setBusy(null);
    }
  };

  const picker = (key: string, label: string) => (
    <select
      value={picks[key] || ''}
      onChange={(e) => setPicks((p) => ({ ...p, [key]: e.target.value }))}
      aria-label={label}
      className="min-h-[44px] min-w-0 rounded-lg border border-gray-300 bg-white px-2 text-xs text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 sm:min-h-0 sm:py-1"
    >
      <option value="">Reassign to…</option>
      {recruiters.map((r) => <option key={r.user_id} value={r.user_id}>{r.name || r.email}</option>)}
    </select>
  );

  const goBtn = (key: string, onClick: () => void) => (
    <button
      type="button"
      onClick={onClick}
      disabled={!picks[key] || Boolean(busy)}
      className="inline-flex min-h-[44px] items-center gap-1 rounded-lg border border-gray-300 px-2.5 py-1 text-xs font-medium text-gray-700 transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700 sm:min-h-0"
    >
      {busy === key && <Loader2 className="h-3 w-3 animate-spin" />} Reassign
    </button>
  );

  return (
    <div className="mt-4 space-y-3 rounded-xl border border-amber-200 bg-amber-50/60 p-3 dark:border-amber-800 dark:bg-amber-900/10">
      <div className="flex items-start justify-between gap-2">
        <p className="flex items-start gap-1.5 text-sm font-medium text-gray-900 dark:text-gray-100">
          <UserMinus className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-600 dark:text-amber-400" />
          This study uses the scheduling assistant, so everyone on this list can be booked.
        </p>
        <button type="button" onClick={onDismiss} className="text-xs font-medium text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200">
          Dismiss
        </button>
      </div>

      {report.error && (
        <Alert tone="warning" size="sm" icon={false}>
          The list was saved, but the calls and leads of anyone you removed could not be listed. Check their calls on the leads themselves.
        </Alert>
      )}
      {failing.length > 0 && (
        <Alert tone="warning" size="sm" icon={false}>
          The assistant can no longer run on this study until this is fixed: {failing.map((i) => i.detail).join('; ')}.
        </Alert>
      )}
      {error && <Alert tone="error" size="sm" icon={false}>{error}</Alert>}

      {removed.map((m) => (
        <div key={m.user_id}>
          <p className="text-xs text-gray-600 dark:text-gray-300">
            <span className="font-semibold text-gray-900 dark:text-gray-100">{m.name || 'A recruiter'}</span> was taken off the list.
            Their calls and leads below stay with them until you reassign them.
          </p>
          <ul className="mt-1.5 divide-y divide-amber-100 rounded-lg border border-amber-100 bg-white dark:divide-gray-700 dark:border-gray-700 dark:bg-gray-800">
            {m.calls.map((c) => {
              const key = `cb:${c.callback_id}`;
              const offerLead = c.owner_is_assignee && Boolean(picks[key]) && !done[key];
              return (
                <li key={key} className="flex flex-wrap items-center gap-2 px-2.5 py-1.5 text-xs">
                  <span className="min-w-0 flex-1">
                    <Link to={`/crm/contacts/${c.contact_id}`} className="font-medium text-primary-700 hover:underline dark:text-primary-400">{c.contact_name}</Link>
                    <span className="text-gray-500 dark:text-gray-400"> · {removedCallOrigin(c.booked_via)} {c.label}{c.study_unknown ? ' (study not recorded)' : ''}</span>
                  </span>
                  {done[key]
                    ? <span className="font-medium text-green-700 dark:text-green-400">{done[key]}</span>
                    : <>{picker(key, `Reassign ${c.contact_name}'s call`)}{goBtn(key, () => reassignCall(c))}</>}
                  {offerLead && (
                    <label className="flex w-full items-start gap-2 text-xs text-gray-700 dark:text-gray-300">
                      <input
                        type="checkbox"
                        checked={keepLead[key] !== false}
                        onChange={(e) => setKeepLead((k) => ({ ...k, [key]: e.target.checked }))}
                        className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 dark:border-gray-600"
                      />
                      <span>
                        Also make {nameOf(picks[key])} the lead&apos;s owner
                        <span className="block text-[11px] text-gray-500 dark:text-gray-400">
                          The lead is still {m.name || 'the removed recruiter'}&apos;s. Whoever takes the call usually owns the lead.
                        </span>
                      </span>
                    </label>
                  )}
                </li>
              );
            })}
            {m.leads.map((l) => {
              const key = `lead:${l.contact_id}`;
              return (
                <li key={key} className="flex flex-wrap items-center gap-2 px-2.5 py-1.5 text-xs">
                  <span className="min-w-0 flex-1">
                    <Link to={`/crm/contacts/${l.contact_id}`} className="font-medium text-primary-700 hover:underline dark:text-primary-400">{l.contact_name}</Link>
                    <span className="text-gray-500 dark:text-gray-400"> · open lead on this study</span>
                  </span>
                  {done[key]
                    ? <span className="font-medium text-green-700 dark:text-green-400">{done[key]}</span>
                    : <>{picker(key, `Reassign ${l.contact_name}`)}{goBtn(key, () => reassignLead(l))}</>}
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </div>
  );
}
